import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {FormInputType} from "../_directives/formInput.directive";
import {Temporal} from "@js-temporal/polyfill";

export const EMAIL_PATTERN = /^[^\s@]+@(?:[^\s@.-](?:[^\s@.]*[^\s@.-])?\.)+(?=.{2,}$)[^\s@.-](?:[^\s@.]*[^\s@.-])?$/u;

export function matchValidator(matchTo: string, reverse?: boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.parent && reverse) {
      const c = (control.parent?.controls as any)[matchTo] as AbstractControl;
      if (c) c.updateValueAndValidity();

      return null;
    }
    return !!control.parent && !!control.parent.value && control.value === (control.parent?.controls as any)[matchTo].value ? null : { error: 'Passwords don\'t match' };
  };
}

export function startDateEndDateValidator(otherDate: string, isThisStartDate?: boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.parent) {
      const otherDateControl = (control.parent?.controls as any)[otherDate] as AbstractControl;
      if (otherDateControl?.value && control.value) {
        let startDate = isThisStartDate ? new Date(control.value).getTime() : new Date(otherDateControl.value).getTime();
        let endDate = isThisStartDate ? new Date(otherDateControl.value).getTime() : new Date(control.value).getTime();

        if (startDate <= endDate) {
          return null;
        }
        else return {error: 'Invalid date selected'};
      } else {
        return null;
      }
    } return null;
  };
}

export function emailValidatorByPattern(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value && EMAIL_PATTERN.test(control.value)) return null;
    else return {error: FORM_ERRORS.EMAIL.PATTERN_ERROR}
  };
}

export function requiredValidator() {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim().length === 0) ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return { error: 'This field is required' };
    }

    return null;
  };
}


export function dateValidator() {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === 'INVALID_DATE') return {error: 'Invalid date'};
    else return null;
  };
}

export function minLengthValidator(fieldName: FormInputType) {
  return (control: AbstractControl): ValidationErrors | null => {
    // @ts-ignore
    if (!control.value || control.value.length < FORM_ERRORS[fieldName].MIN_LENGTH.VALUE) return {error: FORM_ERRORS[fieldName].MIN_LENGTH.MESSAGE};
    else return null;
  };
}

export function minLengthOptionalValidator(fieldName: FormInputType) {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value ?? '').trim();

    if (value === '') return null;
    // @ts-ignore
    const min = FORM_ERRORS[fieldName].MIN_LENGTH.VALUE;

    if (value.length < min) {
      // @ts-ignore
      return { error: FORM_ERRORS[fieldName].MIN_LENGTH.MESSAGE };
    }

    return null;
  };
}

export function maxLengthValidator(fieldName: FormInputType) {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value ?? '';
    // @ts-ignore
    const max = FORM_ERRORS[fieldName].MAX_LENGTH_VALUE;

    if (value.length > max) {
      return { error: `Must be at most ${max} characters long.` };
    }

    return null;
  };
}

function hasCustomRequired(ctrl: AbstractControl | null): boolean {
  if (!ctrl || !ctrl.validator) return false;
  const test = ctrl.validator({ value: null } as AbstractControl);
  return !!test && test['error'] === 'This field is required';
}


export function dateRangeValidator(startKey: string, endKey: string): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const startCtrl = group.get(startKey);
    const endCtrl   = group.get(endKey);

    const start = startCtrl?.value;
    const end   = endCtrl?.value;

    const hasRequiredStart = hasCustomRequired(startCtrl);
    const hasRequiredEnd   = hasCustomRequired(endCtrl);

    const clear = (ctrl: AbstractControl | null) => {
      if (!ctrl) return;
      if (ctrl.errors && ctrl.errors['error']) {
        if (ctrl.errors['error'] !== 'This field is required') {
          ctrl.setErrors(null);
        }
      }
    };

    clear(startCtrl);
    clear(endCtrl);

    // 🎯 CASE 0 — both empty → valid
    if (!start && !end) {
      if (hasRequiredStart || hasRequiredEnd) return null;
      return null;
    }

    // 🎯 CASE 1 — start = INVALID
    if (start === 'INVALID_DATE') {
      startCtrl?.setErrors({ error: 'Invalid Start date' });
      return { dateGroupError: true };
    }

    // 🎯 CASE 2 — end = INVALID
    if (end === 'INVALID_DATE') {
      endCtrl?.setErrors({ error: 'Invalid End date' });
      return { dateGroupError: true };
    }

    // 🎯 CASE 3 — one date is present → both are required
    if (start && !end) {
      endCtrl?.setErrors({
        ...(endCtrl?.errors ?? {}),
        error: 'End date is required'
      });
      return { dateGroupError: true };
    }

    if (!start && end) {
      startCtrl?.setErrors({
        ...(startCtrl?.errors ?? {}),
        error: 'Start date is required'
      });
      return { dateGroupError: true };
    }

    // 🎯 CASE 4 — both lines → trying to parse
    try {
      const startDate = Temporal.PlainDate.from(start);
      const endDate = Temporal.PlainDate.from(end);

      if (Temporal.PlainDate.compare(startDate, endDate) > 0) {
        endCtrl?.setErrors({ error: 'End date cannot be earlier than Start date' });
        return { dateGroupError: true };
      }

      return null;
    } catch {
      // reinsurance
      return null;
    }
  };
}


export const FORM_ERRORS = {
  EMAIL: {
    PATTERN_ERROR: "Invalid email format. Please use the format example@example.com.",
    MAX_LENGTH_VALUE: 50
  },
  PASSWORD: {
    MIN_LENGTH: {
      VALUE: 6,
      MESSAGE: "Password must be at least 6 characters long."
    },
    MAX_LENGTH_VALUE: 40
  },
  NAME: {
    MIN_LENGTH: {
      VALUE: 2,
      MESSAGE: "Name must be at least 2 characters long."
    },
    MAX_LENGTH_VALUE: 30
  },
  TASK_NAME: {
    MIN_LENGTH: {
      VALUE: 2,
      MESSAGE: "Name must be at least 2 characters long."
    },
    MAX_LENGTH_VALUE: 100
  },
  MATERIAL_AREA_NAME: {
    MIN_LENGTH: {
      VALUE: 2,
      MESSAGE: "Name must be at least 2 characters long."
    },
    MAX_LENGTH_VALUE: 60
  },
  BOARD_NOTE_NAME: {
    MIN_LENGTH: {
      VALUE: 2,
      MESSAGE: "Name must be at least 2 characters long."
    },
    MAX_LENGTH_VALUE: 50
  },
  LINK_NAME: {
    MIN_LENGTH: {
      VALUE: 2,
      MESSAGE: "Name must be at least 2 characters long."
    },
    MAX_LENGTH_VALUE: 50
  },
  FLOOR_UNIT_NAME: {
    MIN_LENGTH: {
      VALUE: 1,
      MESSAGE: "Name must be at least 1 characters long."
    },
    MAX_LENGTH_VALUE: 15
  },
  ROOM_AREA_NAME: {
    MIN_LENGTH: {
      VALUE: 1,
      MESSAGE: "Name must be at least 1 characters long."
    },
    MAX_LENGTH_VALUE: 30
  },
  ADDRESS: {
    MIN_LENGTH: {
      VALUE: 5,
      MESSAGE: "Address must be at least 5 characters long."
    },
    MAX_LENGTH_VALUE: 50
  },
  ZIP_CODE: {
    MIN_LENGTH: {
      VALUE: 5,
      MESSAGE: "ZIP code should be 5 digits long."
    },
    MAX_LENGTH_VALUE: 5
  },
  PHONE: {
    MIN_LENGTH: {
      VALUE: 10,
      MESSAGE: "Phone number must be 10 digits long."
    },
    MAX_LENGTH_VALUE: 10
  },
  CUSTOM_PLAN_MAX_USERS: {
    MIN_LENGTH: {
      VALUE: 1,
      MESSAGE: "Number of users should be at least 1 digits long."
    },
    MAX_LENGTH_VALUE: 6
  },
  TRADE_COMPANY_STYLE_MAX_USERS: {
    MIN_LENGTH: {
      VALUE: 1,
      MESSAGE: "Number of users should be at least 1 digits long."
    },
    MAX_LENGTH_VALUE: 3
  },
  PROJECT_CODE: {
    MAX_LENGTH_VALUE: 4
  },
  PROJECT_NUMBER: {
    MAX_LENGTH_VALUE: 50
  },
  TASK_DESCRIPTION: {
    MAX_LENGTH_VALUE: 800
  },
  MATERIAL_DESCRIPTION: {
    MAX_LENGTH_VALUE: 200
  },
  MATERIAL_QUANTITY: {
    MAX_LENGTH_VALUE: 30
  },
  MEASURES: {
    MIN_LENGTH: {
      VALUE: 1,
      MESSAGE: "Name must be at least 1 characters long."
    },
    MAX_LENGTH_VALUE: 15
  }
}
